import { z } from "zod";

export const API_PREFIX = "/v1";

/** Request headers the client sends so the server can pick matching data/rules. */
export const HEADER_CLIENT_BUILD = "x-client-build";
export const HEADER_DATA_VERSION = "x-data-version";

export const HealthResponseSchema = z.object({
  ok: z.literal(true),
  service: z.string(),
  version: z.string(),
  dataVersion: z.string(),
  /** Short git sha the image was built from (`--build-arg GIT_COMMIT`); absent in local dev. */
  commit: z.string().optional(),
  time: z.string().datetime(),
  /** Present only when the server runs as `local` or `test` (never names dev/prod). */
  appEnv: z.enum(["local", "test"]).optional(),
  /** Local only: new signups are verified on the spot (SIGNUP_VERIFY=auto). */
  autoVerify: z.literal(true).optional(),
});
export type HealthResponse = z.infer<typeof HealthResponseSchema>;

/**
 * Remote config: the server's view of feature flags and data version.
 * Flags here override the defaults baked into the data bundle (feature_flags.csv).
 */
export const MaintenanceSchema = z.object({
  active: z.boolean(),
  message: z.string().optional(),
});
export type Maintenance = z.infer<typeof MaintenanceSchema>;

export const RemoteConfigResponseSchema = z.object({
  flags: z.record(z.string(), z.boolean()),
  dataVersion: z.string(),
  serverTime: z.string().datetime(),
  /** Clients below this semantic version must update (the server also answers 426 to them). */
  minClientVersion: z.string(),
  /** Kill switch: while active, authenticated routes answer 503 and the client shows a wall. */
  maintenance: MaintenanceSchema,
  /**
   * The server's build (commit) when it requires clients of the same build (REQUIRE_SAME_BUILD):
   * a web client on another build reloads into this one. Absent: no build check.
   */
  build: z.string().optional(),
  /**
   * The wire's compatibility number (PROTOCOL_VERSION) and the oldest the server still serves
   * (MIN_PROTOCOL): a native build must be in that range.
   */
  protocol: z.number().int().optional(),
  minProtocol: z.number().int().optional(),
});
export type RemoteConfigResponse = z.infer<typeof RemoteConfigResponseSchema>;

/** Error codes the client reacts to globally (services/appGate.ts). */
export const ERROR_UPGRADE_REQUIRED = "upgrade_required";
export const ERROR_MAINTENANCE = "maintenance";

export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

export function apiError(code: string, message: string): ApiError {
  return { error: { code, message } };
}
